import { Component, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ChipItem } from '@app/components/chip-filters/chip-filters-list.component';
import { AnalyticEvents, FeatureFlag, TaskFilterTypes, TaskStatusTypes } from '@app/enums';
import { NotificationCategory } from '@app/models/common/notification-category.model';
import { Category, NotificationRequestFilterOptions } from '@app/modules/dashboard/meta/notification-filter.meta';
import { AnalyticService, AuthService, NotificationService, NotifyService, TasksService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { NotificationType } from '@app/services/notification.service';
import { NotificationViewerComponent, TaskViewerComponent } from '@dashboard/components';
import { Subscription } from 'rxjs';
import { FilterByDateRange, FiltersByDateRange } from './../../meta/notification-filter.meta';
import { NotificationCategoryService } from './../../services/notification-category.service';
import { TranslateService } from '@ngx-translate/core';

type Tabs = 'notifications' | 'tasks';
const notificationsTab = 'notifications';
const tasksTab = 'tasks';
export type ChipFilter = { displayName: string; value: string };

@Component({
    selector: 'app-inbox-viewer',
    templateUrl: './inbox-viewer.template.html',
    styleUrls: ['./inbox-viewer.style.scss'],
})
export class InboxViewerComponent {
    @ViewChild(NotificationViewerComponent, { static: false })
    notificationViewer?: NotificationViewerComponent;
    @ViewChild(TaskViewerComponent, { static: false }) taskViewer?: TaskViewerComponent;

    taskTabTypes = TaskStatusTypes;

    activeTab: Tabs = notificationsTab;

    isAdmin = false;
    routeParamsSubscription?: Subscription;
    totalNumberOfUnreadNotifications = 0;
    totalNumberOfUnreadArchivedNotifications = 0;
    showTaskId?: number;
    chips: ChipItem<NotificationType>[] = [
        { displayValue: 'dashboard.all', value: 'all' },
        { displayValue: 'dashboard.unread', value: 'unread' },
    ];

    notificationDateFilters = FiltersByDateRange;
    notificationCategories: NotificationCategory[] = [];
    isFilterActive = false;

    filterOptions: NotificationRequestFilterOptions = null;
    chipsToDisplay: ChipFilter[] = [];

    isLoading = false;

    hasImprovedHomepageFlag = false;

    constructor(
        private analyticService: AnalyticService,
        public notificationService: NotificationService,
        private featureService: FeatureService,
        private notify: NotifyService,
        private auth: AuthService,
        private route: ActivatedRoute,
        private notificationCategoryService: NotificationCategoryService,
        private router: Router,
        private translateService: TranslateService,
        public tasksService: TasksService
    ) {
        this.notificationService.filterOptions$.pipe(takeUntilDestroyed()).subscribe((filterOptions) => {
            this.filterOptions = filterOptions;

            this.isFilterActive = this.hasActiveFilters(filterOptions);

            if (!filterOptions) {
                this.chipsToDisplay = [];
                return;
            }

            this.chipsToDisplay = this.getChipFilters(filterOptions);
        });
    }

    async ngOnInit(): Promise<void> {
        this.analyticService.trackEvent(AnalyticEvents.SelfServeInboxClickInbox);

        await this.notificationCategoryService.getData();
        this.notificationCategories = this.notificationCategoryService.getLatestCategories();

        this.hasImprovedHomepageFlag = await this.featureService.has(FeatureFlag.improvedHomepage);

        if (this.auth.isAdmin()) {
            this.isAdmin = true;
        }

        if (this.activeTab === 'notifications') {
            this.getUnreadNotificationsCount();
            this.getUnreadArchivedNotificationsCount();
        }

        // set active tab based on url params
        this.routeParamsSubscription = this.route.queryParams.subscribe((params) => {
            if (params['showTasks']) {
                this.setActiveTab(tasksTab);
                this.taskViewer?.onChangeTab(this.taskTabTypes.incomplete);
                this.showTaskId = params['taskId'];
            }
        });
    }

    ngOnDestroy(): void {
        this.routeParamsSubscription?.unsubscribe();
    }

    navigateToAllTasksView(): void {
        this.analyticService.trackEvent(AnalyticEvents.DashboardTaskNotificationsAllTasks);
        this.router.navigate(['/employees/tasks'], {
            relativeTo: this.route,
        });
    }

    onMarkAllAsRead(): void {
        this.analyticService.trackEvent(AnalyticEvents.NotificationsMarkAllRead);
        this.notificationService
            .markAllAsRead()
            .then(() => {
                this.notificationViewer?.reloadCurrentNotifications();
            })
            .catch(() => this.notify.error('Unable to mark all notifications as read.'));
    }

    onArchiveAll(): void {
        this.analyticService.trackEvent(AnalyticEvents.NotificationsMarkAllArchived);
        this.notificationViewer?.onArchiveAll();
    }

    async getUnreadNotificationsCount(): Promise<void> {
        try {
            await this.notificationService.getUnreadNotificationsCount();
            this.totalNumberOfUnreadNotifications = this.notificationService.totalNumberOfUnreadNotifications;
        } catch (err) {
            this.notify.error(err);
        }
    }

    async getUnreadArchivedNotificationsCount(): Promise<void> {
        try {
            await this.notificationService.getUnreadArchivedNotificationsCount();

            this.totalNumberOfUnreadArchivedNotifications =
                this.notificationService.totalNumberOfUnreadArchivedNotifications;
        } catch (err) {
            this.notify.error(err);
        }
    }

    setActiveTab(tab: Tabs): void {
        this.activeTab = tab;

        if (tab == 'tasks') {
            this.taskViewer?.onChangeTab(this.taskTabTypes.incomplete);
        }
    }

    onToggleTaskFilter(taskFilter: TaskFilterTypes): void {
        if (!this.taskViewer) {
            return;
        }
        this.taskViewer.onChangeTaskFilter(taskFilter);
    }

    onDateFilter(notificationDateFilter: FilterByDateRange): void {
        if (notificationDateFilter.value === 'all') {
            this.analyticService.trackEvent(AnalyticEvents.InboxFilterByDateRangeAll);
            this.notificationService.clearFilterOptions();
            this.isFilterActive = this.hasActiveFilters(this.filterOptions);
            return;
        }

        switch (notificationDateFilter.value) {
            case 'lastSevenDays':
                this.analyticService.trackEvent(AnalyticEvents.InboxFilterByDateRangeLast7);
                break;
            case 'lastThirtyDays':
                this.analyticService.trackEvent(AnalyticEvents.InboxFilterByDateRangeLast30);
                break;
        }

        this.notificationService.setFilterOptions({
            ...this.filterOptions,
            dateRangeData: {
                displayName: notificationDateFilter.displayName,
                value: notificationDateFilter.value,
                from: notificationDateFilter.from,
                to: notificationDateFilter.to,
            },
        });

        this.isFilterActive = this.hasActiveFilters(this.filterOptions);
    }

    onCategoryFilter(category: Category): void {
        // If user selects `all` then wipe the filters
        if (category.id === -1) {
            this.analyticService.trackEvent(AnalyticEvents.InboxFilterByCategoryAll);
            this.notificationService.clearFilterOptions();
            return;
        }

        this.analyticService.trackEvent(
            this.formatEvent(AnalyticEvents.InboxFilterByCategoryOption, {
                name: category.displayName,
            })
        );

        const currentCategories = this.filterOptions?.categories || [];

        // Check if the category already exists
        const categoryExists = currentCategories.some((cat) => cat.id === category.id);

        // Add the new category if it doesn't exist
        const newCategories = categoryExists ? currentCategories : [...currentCategories, category];

        if (!categoryExists) {
            this.notificationService.setFilterOptions({
                ...this.filterOptions,
                categories: newCategories,
            });
        }

        this.isFilterActive = this.hasActiveFilters(this.filterOptions);
    }

    formatEvent(event: string, params: { [key: string]: string }): AnalyticEvents {
        const formattedEvent = event.replace(/{{(\w+)}}/g, (_, key) => params[key] || `{{${key}}}`);
        return formattedEvent as AnalyticEvents;
    }

    hasActiveFilters(filterOptions: NotificationRequestFilterOptions): boolean {
        if (!filterOptions) {
            return false;
        }

        const { dateRangeData, categories } = filterOptions;

        // Check if dateRangeData exists
        const isDateRangeActive = !!dateRangeData;

        // Check if categories array has any active filters
        const isCategoryIdsActive = !!categories && categories.length > 0;

        return isDateRangeActive || isCategoryIdsActive;
    }

    goToArchivePage(): void {
        this.analyticService.trackEvent(AnalyticEvents.NotificationsGoToArchived);
        this.router.navigate(['/dashboard/archived-notifications'], {
            relativeTo: this.route,
        });
    }

    goToCompletedTasksPage(): void {
        this.analyticService.trackEvent(AnalyticEvents.DashboardTaskNotificationViewedCompletedTasks);
        this.router.navigate(['/dashboard/completed-tasks'], {
            relativeTo: this.route,
        });
    }

    removeFilter(chip: ChipFilter): void {
        if (!this.filterOptions) {
            return;
        }

        // If the user wants to clear the filters
        if (chip.value === 'clear') {
            this.notificationService.clearFilterOptions();
            this.isFilterActive = this.hasActiveFilters(this.filterOptions);
            return;
        }

        // Filter out the category with the specified id
        const newCategories = this.filterOptions.categories?.filter(
            (category) => category.id.toString() !== chip.value
        );

        const newDateRangeData =
            this.filterOptions.dateRangeData?.value === chip.value ? undefined : this.filterOptions.dateRangeData;

        this.notificationService.setFilterOptions({
            ...this.filterOptions,
            categories: newCategories,
            dateRangeData: newDateRangeData,
        });

        this.isFilterActive = this.hasActiveFilters(this.filterOptions);
    }

    private getChipFilters(filterOptions: NotificationRequestFilterOptions): ChipFilter[] {
        const chips: ChipFilter[] = [];

        if (filterOptions?.dateRangeData) {
            chips.push({
                displayName: filterOptions.dateRangeData.displayName,
                value: filterOptions.dateRangeData.value,
            });
        }

        if (filterOptions?.categories?.length) {
            filterOptions.categories.forEach((category) => {
                chips.push({
                    displayName: category.displayName,
                    value: category.id.toString(),
                });
            });
        }

        return chips;
    }
}
